// DF HACCP SafeGuard — copia dei report sul dispositivo (Cache Storage del browser).
// Serve a Ispezione ASL/NAS quando il server non è raggiungibile: si apre da
// /ispezione-offline senza login, con i soli documenti già scaricati qui.
// Ogni PDF è verificato con lo SHA-256 del registro prima di salvarlo e quando si apre.
// La copia resta anche dopo "Esci" (serve proprio quando non si riesce ad accedere); si toglie
// con "Rimuovi i documenti da questo dispositivo" o quando entra un utente di un'altra azienda.
// Limiti reali: il browser può cancellare questi dati (spazio, impostazioni, iPhone
// se l'app non è installata nella Home); non sono un archivio, solo una copia di comodo.
import api from "./api";

const CACHE = "df-safeguard-docs-v1";
const INDEX_URL = "/__safeguard/index.json";
const LAST_SYNC_KEY = "df-safeguard-last-sync";
const SYNC_EVERY_MS = 3 * 60 * 60 * 1000;
export const KEEP_DAILY = 31;
export const KEEP_MONTHLY = 3;

const pdfUrl = (id) => `/__safeguard/${id}.pdf`;
// Senza ".pdf" nel percorso: non viene toccata dalla pulizia dei report non più da tenere.
const EMERGENCY_URL = "/__safeguard/procedura-emergenza";
// Archivio Ispezione ASL/NAS: non per gli addetti (stessa regola del server).
export const canSeeArchive = (user) => ["super_admin", "titolare", "responsabile", "ispettore"].includes(user?.role);
const supported = () => typeof window !== "undefined" && "caches" in window && !!window.crypto?.subtle;

export async function sha256Hex(buffer) {
  const digest = await window.crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function readIndex() {
  if (!supported()) return null;
  try {
    const cache = await caches.open(CACHE);
    const res = await cache.match(INDEX_URL);
    return res ? await res.json() : null;
  } catch {
    return null;
  }
}

// PDF salvato sul dispositivo, ricontrollato con l'impronta registrata.
export async function readPdf(doc) {
  const cache = await caches.open(CACHE);
  const res = await cache.match(pdfUrl(doc.id));
  if (!res) return { ok: false, reason: "missing" };
  const buffer = await res.arrayBuffer();
  const verified = (await sha256Hex(buffer)) === doc.sha256;
  return { ok: verified, reason: verified ? null : "hash", blob: new Blob([buffer], { type: "application/pdf" }) };
}

// Procedura di emergenza salvata sul dispositivo (si apre anche senza server).
export async function readEmergency() {
  if (!supported()) return null;
  try {
    const res = await (await caches.open(CACHE)).match(EMERGENCY_URL);
    return res ? new Blob([await res.arrayBuffer()], { type: "application/pdf" }) : null;
  } catch {
    return null;
  }
}

export async function clearDeviceCopy() {
  try { localStorage.removeItem(LAST_SYNC_KEY); } catch { /* storage non disponibile */ }
  if (supported()) await caches.delete(CACHE).catch(() => {});
}

export async function storageInfo() {
  const info = { persisted: null, usage: null, quota: null };
  try {
    if (navigator.storage?.persisted) info.persisted = await navigator.storage.persisted();
    if (navigator.storage?.estimate) Object.assign(info, await navigator.storage.estimate());
  } catch { /* non supportato */ }
  return info;
}

// Allinea la copia sul dispositivo agli ultimi documenti verificati sul server.
// Non cancella nulla di ciò che è ancora fra i documenti da tenere; scarica solo ciò che manca.
export async function syncDeviceCopy(user, { force: forced = false } = {}) {
  let force = forced;
  if (!supported() || !user || user.impersonated_by || !navigator.onLine) return null;
  const previous = await readIndex();
  if (previous?.tenant_id && previous.tenant_id !== user.tenant_id) {
    // Dispositivo passato a un'altra azienda: la copia precedente sparisce subito, prima di ogni altro controllo.
    await caches.delete(CACHE);
    force = true;
  }
  try {
    const last = Number(localStorage.getItem(`${LAST_SYNC_KEY}-${user.tenant_id}`) || 0);
    if (!force && Date.now() - last < SYNC_EVERY_MS) return null;
  } catch { /* storage non disponibile: si sincronizza comunque */ }

  // Addetto: si aggiorna solo la procedura di emergenza. I report salvati qui da un titolare o
  // responsabile della stessa azienda (es. tablet di cucina condiviso) restano come sono.
  const archive = canSeeArchive(user);
  const sameTenant = previous?.tenant_id === user.tenant_id;
  let status = sameTenant ? { company_name: previous.company_name, last_verified_at: previous.last_verified_at,
    dfdrive: { share_url: previous.share_url, last_copy_at: previous.last_copy_at } } : {};
  let keep = sameTenant ? previous.documents || [] : [];
  if (archive) {
    const [{ data: fresh }, { data: daily }, { data: monthly }] = await Promise.all([
      api.get("/app/safeguard/status"),
      api.get("/app/safeguard/documents", { params: { kind: "daily" } }),
      api.get("/app/safeguard/documents", { params: { kind: "monthly" } }),
    ]);
    status = fresh;
    keep = [...daily.documents.slice(0, KEEP_DAILY), ...monthly.documents.slice(0, KEEP_MONTHLY)];
  }
  const cache = await caches.open(CACHE);
  for (const doc of archive ? keep : []) {
    if (await cache.match(pdfUrl(doc.id))) continue;
    const { data } = await api.get(`/app/safeguard/documents/${doc.id}/pdf`, { responseType: "arraybuffer" });
    if ((await sha256Hex(data)) !== doc.sha256) continue; // mai salvare una copia non integra
    await cache.put(pdfUrl(doc.id), new Response(data, { headers: { "Content-Type": "application/pdf" } }));
  }
  const keepIds = new Set(keep.map((d) => d.id));
  for (const request of await cache.keys()) {
    const id = new URL(request.url).pathname.match(/^\/__safeguard\/(.+)\.pdf$/)?.[1];
    if (id && !keepIds.has(id)) await cache.delete(request);
  }
  let emergencySavedAt = previous?.tenant_id === user.tenant_id ? previous?.emergency_saved_at || null : null;
  try {
    const { data } = await api.get("/app/safeguard/emergency-procedure", { responseType: "arraybuffer" });
    await cache.put(EMERGENCY_URL, new Response(data, { headers: { "Content-Type": "application/pdf" } }));
    emergencySavedAt = new Date().toISOString();
  } catch { /* resta l'eventuale copia precedente */ }
  let persisted = null;
  try { persisted = navigator.storage?.persist ? await navigator.storage.persist() : null; } catch { persisted = null; }
  const stored = [];
  for (const doc of keep) if (await cache.match(pdfUrl(doc.id))) stored.push(doc);
  const index = {
    tenant_id: user.tenant_id, company_name: status.company_name || null, saved_at: new Date().toISOString(), persisted,
    share_url: status.dfdrive?.share_url || null, last_verified_at: status.last_verified_at,
    last_copy_at: status.dfdrive?.last_copy_at || null, emergency_saved_at: emergencySavedAt,
    documents: stored.map(({ id, kind, period_key, version, doc_number, sha256, generated_at, data_cutoff, filename, counts }) =>
      ({ id, kind, period_key, version, doc_number, sha256, generated_at, data_cutoff, filename, counts })),
  };
  await cache.put(INDEX_URL, new Response(JSON.stringify(index), { headers: { "Content-Type": "application/json" } }));
  try { localStorage.setItem(`${LAST_SYNC_KEY}-${user.tenant_id}`, String(Date.now())); } catch { /* storage non disponibile */ }
  return index;
}

// Apre un PDF (blob) in una scheda già aperta al clic (evita il blocco popup di Safari).
export function openBlob(blob, win) {
  const url = URL.createObjectURL(blob);
  if (win) win.location.href = url; else window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
